import { UpdatesNotepad } from "@/components/UpdatesNotepad";
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation, Navigate } from "react-router-dom";
import { MobileTopBar, useMobileMenu } from "@/components/MobileTopBar";
import { useAuth } from "@/AuthContext";
import { api } from "@/api";
import { LayoutDashboard, Users, BookOpen, Gavel, UserCircle2, LogOut, Handshake, Euro, FileSignature, Globe, PanelsTopLeft, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BpPasswordGate } from "@/components/bp/BpPasswordGate";

const nav = [
  { to: "/bp", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testid: "bp-nav-dashboard" },
  { to: "/bp/leads", label: "Mes leads", icon: Users, testid: "bp-nav-leads" },
  { to: "/bp/gains", label: "Mes gains", icon: Euro, testid: "bp-nav-gains" },
  { to: "/bp/minisite", label: "Mon mini-site", icon: Globe, testid: "bp-nav-minisite" },
  { to: "/bp/site", label: "Site web Pro", icon: PanelsTopLeft, testid: "bp-nav-site" },
  { to: "/bp/catalogue", label: "Catalogue PBM", icon: BookOpen, testid: "bp-nav-catalog" },
  { to: "/bp/billetterie", label: "Billetterie & Parcs", icon: TreePine, testid: "bp-nav-billetterie" },
  { to: "/bp/contrat", label: "Mon contrat", icon: FileSignature, testid: "bp-nav-contract" },
  { to: "/bp/reglement", label: "Règlement BP", icon: Gavel, testid: "bp-nav-rules" },
  { to: "/bp/profil", label: "Mon profil", icon: UserCircle2, testid: "bp-nav-profile" },
];
const ALLOWED_WHEN_PENDING = ["/bp/reglement", "/bp/profil"];

export default function BpLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mobile = useMobileMenu();
  const [me, setMe] = useState(null);
  const doLogout = async () => { await logout(); navigate("/"); };

  const refresh = useCallback(() => api.get("/bp/me").then((r) => setMe(r.data)).catch(() => setMe({ error: true })), []);
  useEffect(() => { refresh(); }, [refresh, pathname]);

  const rulesPending = me && !me.error && !me.rules?.accepted;
  if (rulesPending && !ALLOWED_WHEN_PENDING.some((p) => pathname.startsWith(p))) return <Navigate to="/bp/reglement" replace />;

  return (
    <div className="dashboard-shell bg-white">
      <MobileTopBar portal="bp" label="Business Partner" open={mobile.open} onToggle={mobile.toggle} dark accent="#FFD700" />
      <aside className={`${mobile.asideClass} border-r border-[#DEE2E6] bg-[#0A0A0A] text-white flex-col`} onClick={mobile.onNavClick} data-testid="bp-sidebar">
        <div className="p-6 border-b border-white/10 hidden lg:block">
          <Link to="/" className="flex items-center gap-3" data-testid="bp-brand">
            <img src="/pag-logo.png" alt="PAG" className="h-9 w-9 object-contain bg-white p-0.5" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#FFD700] border-l border-white/20 pl-3 flex items-center gap-1"><Handshake className="h-3 w-3" /> Partner</span>
          </Link>
        </div>
        <div className="px-6 py-4 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Business Partner</div>
          <div className="font-display font-bold text-sm mt-1 truncate" data-testid="bp-sidebar-name">{user?.prenom} {user?.nom}</div>
          <div className="font-mono text-[11px] text-[#FFD700] mt-0.5" data-testid="bp-sidebar-code">{user?.code}</div>
          {me && !me.error && <div className="mt-2 inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-white/10" data-testid="bp-sidebar-status">{me.status_label}</div>}
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                     className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-white text-[#0A0A0A]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <item.icon className="h-4 w-4" /> {item.label}
              {item.to === "/bp/reglement" && rulesPending && <span className="ml-auto w-2 h-2 bg-[#E60000] rounded-full" data-testid="bp-nav-rules-pending" />}
              {item.to === "/bp/contrat" && me && !me.error && (me.contract_needs_signature ?? !me.contract_signed) && <span className="ml-auto w-2 h-2 bg-[#FFD700] rounded-full" data-testid="bp-nav-contract-pending" />}
              {item.to === "/bp/profil" && me && !me.error && me.vigilance && !me.vigilance.ok && me.onboarding?.profile_complete && <span className={`ml-auto w-2 h-2 rounded-full ${me.vigilance.state === "pending" ? "bg-[#FFD700]" : "bg-[#E60000]"}`} title="Attestation de vigilance URSSAF" data-testid="bp-nav-profile-vigilance" />}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <Button onClick={doLogout} data-testid="bp-logout" variant="ghost" className="w-full justify-start text-white/70 hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>
      <main className="overflow-y-auto bg-[#F8F9FA]">
        {!me?.must_change_password && <UpdatesNotepad className="mx-6 lg:mx-10 mt-4" compact />}
        {me?.must_change_password ? <BpPasswordGate onDone={refresh} /> : <Outlet context={{ me, refresh }} />}
      </main>
    </div>
  );
}
